import { createWrapInstruction } from '@metaplex-foundation/mpl-candy-guard';
import { useOperation } from '../../../types/Operation.mjs';
import { TransactionBuilder } from '../../../utils/TransactionBuilder.mjs';

// -----------------
// Operation
// -----------------

const Key = 'WrapCandyGuardOperation';

/**
 * Wraps the given Candy Machine in a Candy Guard.
 *
 * This makes the Candy Guard the mint authority for the Candy Machine
 * which means all minting will have to go through the Candy Guard.
 *
 * ```ts
 * await metaplex
 *   .candyMachines()
 *   .wrapCandyGuard({
 *     candyMachine,
 *     candyGuard,
 *   };
 * ```
 *
 * @group Operations
 * @category Constructors
 */
const wrapCandyGuardOperation = useOperation(Key);

/**
 * @group Operations
 * @category Types
 */

/**
 * @group Operations
 * @category Handlers
 */
const wrapCandyGuardOperationHandler = {
  async handle(operation, metaplex, scope) {
    return wrapCandyGuardBuilder(metaplex, operation.input, scope).sendAndConfirm(metaplex, scope.confirmOptions);
  }
};

// -----------------
// Builder
// -----------------

/**
 * @group Transaction Builders
 * @category Inputs
 */

/**
 * Wraps the given Candy Machine in a Candy Guard.
 *
 * This makes the Candy Guard the mint authority for the Candy Machine
 * which means all minting will have to go through the Candy Guard.
 *
 * ```ts
 * const transactionBuilder = await metaplex
 *   .candyMachines()
 *   .builders()
 *   .wrapCandyGuard({
 *     candyMachine,
 *     candyGuard,
 *   });
 * ```
 *
 * @group Transaction Builders
 * @category Constructors
 */
const wrapCandyGuardBuilder = (metaplex, params, options = {}) => {
  const {
    programs,
    payer = metaplex.rpc().getDefaultFeePayer()
  } = options;
  const {
    candyGuard,
    candyGuardAuthority = metaplex.identity(),
    candyMachine,
    candyMachineAuthority = metaplex.identity()
  } = params;
  const candyMachineProgram = metaplex.programs().getCandyMachine(programs);
  const candyGuardProgram = metaplex.programs().getCandyGuard(programs);
  return TransactionBuilder.make().setFeePayer(payer).add({
    instruction: createWrapInstruction({
      candyGuard,
      authority: candyGuardAuthority.publicKey,
      candyMachine,
      candyMachineProgram: candyMachineProgram.address,
      candyMachineAuthority: candyMachineAuthority.publicKey
    }, candyGuardProgram.address),
    signers: [candyGuardAuthority, candyMachineAuthority],
    key: params.wrapCandyGuardInstructionKey ?? 'wrapCandyGuard'
  });
};

export { wrapCandyGuardBuilder, wrapCandyGuardOperation, wrapCandyGuardOperationHandler };
//# sourceMappingURL=wrapCandyGuard.mjs.map
